import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon, type IconName } from '../Icon';

const detailCardVariants = cva('flex flex-col gap-2 text-left font-noto text-xs', {
  variants: {
    variant: {
      card: 'rounded-lg border border-grey-30 bg-white p-3 shadow-[0_0_4px_rgba(0,0,0,0.25)]',
      plain: '',
    },
  },
  defaultVariants: {
    variant: 'card',
  },
});

export type DetailCardRow = {
  /** Row labels double as React keys, so they must be unique within one card. */
  label: string;
  value?: React.ReactNode;
  'data-testid'?: string;
};

type DetailCardProps = React.ComponentProps<'dl'> &
  VariantProps<typeof detailCardVariants> & {
    /** Label/value rows; omit when the card body is free-form `children`. */
    rows?: DetailCardRow[];
    /** Width of the label column in px, so values align across rows. */
    labelWidth?: number;
  };

function DetailCard({ rows = [], labelWidth = 90, variant, className, children, ...props }: DetailCardProps) {
  return (
    <dl data-slot="detail-card" className={cn(detailCardVariants({ variant }), className)} {...props}>
      {rows.map((row) => {
        const isEmpty = row.value == null || row.value === '';

        return (
          <div key={row.label} className="flex items-start gap-5" data-testid={row['data-testid']}>
            <dt className="shrink-0 font-medium text-grey-60" style={{ width: labelWidth }}>
              {row.label}
            </dt>
            <dd className="min-w-0 flex-1 break-words font-medium text-grey-90">
              {isEmpty ? (
                <>
                  <span aria-hidden="true">-</span>
                  <span className="sr-only">No data</span>
                </>
              ) : (
                row.value
              )}
            </dd>
          </div>
        );
      })}
      {children}
    </dl>
  );
}

// Props are element-agnostic (HTMLElement) because they spread onto either an
// `<a>` or a `<button>`; anchor-only attributes are declared explicitly.
type DetailCardLinkProps = React.HTMLAttributes<HTMLElement> & {
  /** Trailing icon, e.g. `download`, `externalLink`, `envelopeClosed`. */
  icon?: IconName;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  download?: string | boolean;
  'data-testid'?: string;
};

/**
 * Link-styled value for a DetailCard row. Renders a real `<a>` when `href` is
 * given, otherwise a `<button>` (e.g. programmatic downloads via `onClick`).
 */
function DetailCardLink({ icon, href, target, rel, download, children, className, ...props }: DetailCardLinkProps) {
  const classes = cn(
    'inline-flex max-w-full cursor-pointer items-center gap-1 break-all text-blue-50 underline focus-ring',
    className,
  );
  const content = (
    <>
      <span className="min-w-0">{children}</span>
      {icon && <Icon icon={icon} size={16} className="shrink-0" />}
    </>
  );

  if (href) {
    return (
      <a
        data-slot="detail-card-link"
        href={href}
        target={target}
        rel={rel}
        download={download}
        className={classes}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button data-slot="detail-card-link" type="button" className={classes} {...props}>
      {content}
    </button>
  );
}

export { DetailCard, DetailCardLink };
